/**
 * [Phase 2c-B §4.12 NEW-3] Module-level TTS singleton.
 *
 * Vanilla-JS class hung off a module-scope `new TTSPlayer()` export. Webpack/ESM
 * module-level singletons are spec - importing `ttsPlayer` from anywhere in the
 * bundle returns the same instance. Audio survives ANY route change because the
 * HTMLAudioElement lives on this object, not in the component tree.
 *
 * Caching: by `activityId` in an in-memory Map. Cleared on logout via `reset()`.
 * Concurrent identical fetches: deduped via `_inflight` Promise map.
 * Logout: AuthContext.logout() calls `ttsPlayer.reset()` (stops + clears cache).
 */
import { getToken } from "../contexts/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

class TTSPlayer {
  constructor() {
    this._audio = null;
    this._cache = new Map();      // activityId -> audio_b64
    this._inflight = new Map();   // activityId -> Promise
    this._currentActivityId = null;
    this._currentSnippet = null;  // short text preview for NowPlayingIndicator
    this._isPlaying = false;
    this._listeners = new Set();
  }

  _emit() {
    const snapshot = {
      playing: this._isPlaying,
      activityId: this._currentActivityId,
      snippet: this._currentSnippet,
    };
    for (const fn of this._listeners) {
      try { fn(snapshot); } catch (_e) { /* listener errors don't bubble */ }
    }
  }

  subscribe(fn) {
    this._listeners.add(fn);
    // Push current state immediately so new subscribers re-hydrate.
    fn({ playing: this._isPlaying, activityId: this._currentActivityId, snippet: this._currentSnippet });
    return () => this._listeners.delete(fn);
  }

  isPlayingActivity(activityId) {
    return this._isPlaying && this._currentActivityId === activityId;
  }

  async _fetchAudio(voiceText) {
    const res = await fetch(`${BACKEND}/api/tts/speak`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
      },
      body: JSON.stringify({ text: voiceText }),
    });
    if (!res.ok) throw new Error(`TTS fetch failed: ${res.status}`);
    const data = await res.json();
    // [TD-27] backend returns audio_b64; never audio_base64
    return data.audio_b64;
  }

  async play({ activityId, voiceText, snippet }) {
    if (!activityId || !voiceText) return;

    // Stop whatever is currently playing (single-track player).
    this._stopAudioOnly();

    this._currentActivityId = activityId;
    this._currentSnippet = snippet || voiceText.slice(0, 120);
    this._isPlaying = true;
    this._emit();

    let b64 = this._cache.get(activityId);
    if (!b64) {
      // Dedupe concurrent fetches for the same activity.
      let inflight = this._inflight.get(activityId);
      if (!inflight) {
        inflight = this._fetchAudio(voiceText);
        this._inflight.set(activityId, inflight);
      }
      try {
        b64 = await inflight;
        this._cache.set(activityId, b64);
      } catch (e) {
        this._inflight.delete(activityId);
        // Bail if the click context changed during fetch.
        if (this._currentActivityId !== activityId) return;
        this._isPlaying = false;
        this._currentActivityId = null;
        this._currentSnippet = null;
        this._emit();
        return;
      } finally {
        this._inflight.delete(activityId);
      }
      // User may have clicked stop or another row mid-fetch - re-check identity.
      if (this._currentActivityId !== activityId) return;
    }

    if (!this._audio) {
      this._audio = new Audio();
      this._audio.addEventListener("ended", () => {
        this._isPlaying = false;
        this._currentActivityId = null;
        this._currentSnippet = null;
        this._emit();
      });
      this._audio.addEventListener("error", () => {
        this._isPlaying = false;
        this._currentActivityId = null;
        this._currentSnippet = null;
        this._emit();
      });
    }

    this._audio.src = `data:audio/mpeg;base64,${b64}`;
    try {
      await this._audio.play();
    } catch (_e) {
      this._isPlaying = false;
      this._currentActivityId = null;
      this._currentSnippet = null;
      this._emit();
    }
  }

  _stopAudioOnly() {
    if (this._audio) {
      try { this._audio.pause(); } catch (_e) { /* noop */ }
      try { this._audio.currentTime = 0; } catch (_e) { /* noop */ }
    }
  }

  stop() {
    this._stopAudioOnly();
    this._isPlaying = false;
    this._currentActivityId = null;
    this._currentSnippet = null;
    this._emit();
  }

  reset() {
    this.stop();
    this._cache.clear();
    this._inflight.clear();
  }
}

const ttsPlayer = new TTSPlayer();
export default ttsPlayer;
